import type { DispatchInfo, LineItem, Order, OrderStep, Product, Role, User } from '../types';
import { ORDER_STAGES } from '../constants';
import { isSectionComplete } from './productionHelpers';
import {
  getLineItemDispatchMilestone,
  isLineItemDispatchConfirmed,
} from './dispatchHelpers';
import { getDeliveryProgressSteps, getDispatchFlowMode, getLineItemDeliveryType } from './dispatchFlow';
import { orderUsesLineItemWorkflow, getEffectivePipelineStage, isInvoicingComplete } from './stageHelpers';
import type { StagePipelineStatus } from './stageHelpers';
import { deptAssignedToUserForQueue } from './workflowHelpers';

function stageOrder(stage: Order['stage']): number {
  return ORDER_STAGES.find((s) => s.id === stage)?.order ?? 0;
}

export type LineItemFulfillmentStage =
  | 'in_fulfillment'
  | 'ready_for_dispatch'
  | 'dispatched'
  | 'delivered';

export type LineItemPipelineItem =
  | {
      kind: 'workflow';
      stepIndex: number;
      id: string;
      label: string;
      department: string;
    }
  | {
      kind: 'dispatch';
      stage:
        | 'ready_for_dispatch'
        | 'dispatched'
        | 'delivered'
        | 'client_pickup'
        | 'client_confirmed';
      id: string;
      label: string;
    }
  | { kind: 'completed'; id: 'completed'; label: 'Completed' };

/** Departments that belong on the Dispatch tab — not fulfillment workflow pills. */
export const DISPATCH_WORKFLOW_DEPARTMENTS = new Set(['Dispatch', 'Logistics', 'Delivery']);

const DISPATCH_DEPARTMENT_KEYS = new Set(
  [...DISPATCH_WORKFLOW_DEPARTMENTS].map((d) => d.toLowerCase()),
);

function isDispatchLikeStepName(name: string): boolean {
  const n = name.trim().toLowerCase();
  if (n === 'delivery' || n === 'dispatch' || n === 'logistics') return true;
  if (n.startsWith('delivery ') || n.startsWith('dispatch ')) return true;
  return n.includes('delivery / dispatch') || n.includes('delivery/dispatch');
}

export function isDispatchWorkflowDepartment(department: string): boolean {
  return DISPATCH_DEPARTMENT_KEYS.has(department.trim().toLowerCase());
}

/** Post-fulfillment workflow step — by department or legacy step name. */
export function isDispatchWorkflowStep(step: Pick<OrderStep, 'department' | 'name'>): boolean {
  return isDispatchWorkflowDepartment(step.department) || isDispatchLikeStepName(step.name);
}

export function isFulfillmentWorkflowStep(step: Pick<OrderStep, 'department' | 'name'>): boolean {
  return !isDispatchWorkflowStep(step);
}

/** Treat Dispatch, Logistics, and Delivery as equivalent workflow departments. */
const WORKFLOW_STEP_DEPARTMENTS = [
  'Design',
  'Production',
  'Finishing',
  'Quality Check',
  'Installation',
  'Logistics',
] as const;

function normalizeStepDepartment(department: string): string {
  const trimmed = department.trim();
  const match = WORKFLOW_STEP_DEPARTMENTS.find((d) => d.toLowerCase() === trimmed.toLowerCase());
  if (match) return match;
  if (trimmed.toLowerCase() === 'qc') return 'Quality Check';
  return trimmed;
}

export function departmentMatchesWorkflowStep(
  department: string,
  stepDepartment: string,
): boolean {
  const a = normalizeStepDepartment(department);
  const b = normalizeStepDepartment(stepDepartment);
  if (a === b) return true;
  return (
    DISPATCH_WORKFLOW_DEPARTMENTS.has(a) &&
    DISPATCH_WORKFLOW_DEPARTMENTS.has(b)
  );
}

/** Product workflow includes a Dispatch / Logistics / Delivery step. */
export function lineItemHasDispatchInWorkflow(
  item: Pick<LineItem, 'workflow'>,
): boolean {
  return (item.workflow ?? []).some((s) => isDispatchWorkflowStep(s));
}

export function getLineItemLogisticsStepInfo(
  item: Pick<LineItem, 'workflow'>,
): { stepIndex: number; stepName: string } {
  const workflow = item.workflow ?? [];
  const idx = workflow.findIndex((s) => departmentMatchesWorkflowStep('Logistics', s.department));
  if (idx >= 0) {
    return { stepIndex: idx, stepName: workflow[idx].name };
  }
  return { stepIndex: workflow.length, stepName: 'Logistics' };
}

/** Line items that use the delivery handoff flow (not in-store pickup) after fulfillment. */
export function lineItemsWithDeliveryHandoff(
  order: Pick<Order, 'lineItems' | 'deliveryType'>,
): LineItem[] {
  return order.lineItems.filter(
    (li) => (li.workflow?.length ?? 0) > 0 && lineItemNeedsDispatch(order, li),
  );
}

/** @deprecated Use {@link lineItemsWithDeliveryHandoff}. */
export function lineItemsWithDispatchInWorkflow(
  order: Pick<Order, 'lineItems' | 'deliveryType'>,
): LineItem[] {
  return lineItemsWithDeliveryHandoff(order);
}

/** Dispatched or past dispatch — drives Ready for Dispatch → Dispatched pill label. */
export function isLineItemDispatchedForDispatchPill(
  item: Pick<LineItem, 'dispatch'>,
): boolean {
  const status = item.dispatch?.status;
  return status === 'dispatched' || status === 'delivered' || status === 'delivery_failed';
}

export function getLineItemDispatchPillLabel(
  item: Pick<LineItem, 'dispatch'>,
): 'Ready for Dispatch' | 'Dispatched' {
  return isLineItemDispatchedForDispatchPill(item) ? 'Dispatched' : 'Ready for Dispatch';
}

export function getOrderDispatchPillLabel(
  order: Pick<Order, 'lineItems' | 'deliveryType'>,
): 'Ready for Dispatch' | 'Dispatched' {
  const items = lineItemsWithDispatchInWorkflow(order);
  if (!items.length) return 'Ready for Dispatch';
  return items.every(isLineItemDispatchedForDispatchPill) ? 'Dispatched' : 'Ready for Dispatch';
}

/** Fulfillment finished and delivery handoff recorded per line-item delivery type. */
export function allOrderLineItemsPastFulfillmentAndDispatch(
  order: Pick<Order, 'lineItems' | 'deliveryType'>,
): boolean {
  const items = order.lineItems.filter((li) => (li.workflow?.length ?? 0) > 0);
  if (!items.length) return false;
  return items.every((li) => {
    if (!allFulfillmentStepsComplete(li)) return false;
    if (!lineItemNeedsDispatch(order, li)) {
      return li.dispatch?.status === 'delivered';
    }
    return isLineItemDispatchedForDispatchPill(li);
  });
}

/** True when this line item uses dispatch/delivery handoff (vs client pickup). Driven by delivery type. */
export function lineItemNeedsDispatch(
  order: Pick<Order, 'deliveryType'>,
  item: Pick<LineItem, 'deliveryType' | 'workflow'>,
): boolean {
  if (!(item.workflow?.length ?? 0)) return false;
  const delivery = item.deliveryType ?? order.deliveryType;
  return delivery !== 'in_store_pickup';
}

/** Any line item with fulfillment workflow is handled by Logistics after production. */
export function lineItemInLogisticsScope(item: Pick<LineItem, 'workflow'>): boolean {
  return (item.workflow?.length ?? 0) > 0;
}

export function lastFulfillmentStepIndex(workflow: OrderStep[]): number {
  let last = -1;
  for (let i = 0; i < workflow.length; i++) {
    if (isFulfillmentWorkflowStep(workflow[i])) last = i;
  }
  return last;
}

/** True when every non-dispatch workflow step is complete. */
export function allFulfillmentStepsComplete(item: LineItem): boolean {
  const wf = item.workflow ?? [];
  const fulfillmentSteps = wf.filter(isFulfillmentWorkflowStep);
  if (!fulfillmentSteps.length) {
    if (!wf.length) return false;
    return wf.every((s) => isSectionComplete(s.status));
  }
  if (fulfillmentSteps.every((s) => isSectionComplete(s.status))) return true;

  const firstDispatchIdx = wf.findIndex((s) => isDispatchWorkflowStep(s));
  if (firstDispatchIdx > 0) {
    return wf.slice(0, firstDispatchIdx).every((s) => isSectionComplete(s.status));
  }

  const activeIdx = item.currentStepIndex ?? 0;
  if (activeIdx > 0 && activeIdx === wf.length - 1 && !isSectionComplete(wf[activeIdx].status)) {
    if (isDispatchWorkflowStep(wf[activeIdx])) {
      return wf.slice(0, activeIdx).every((s) => isSectionComplete(s.status));
    }
    return false;
  }
  return false;
}

export function getLineItemProductName(
  item: Pick<LineItem, 'productId'>,
  products: Product[],
): string | undefined {
  if (!item.productId) return undefined;
  return products.find((p) => p.id === item.productId)?.name;
}

/** Short label for audits and UI captions. */
export function lineItemDisplayLabel(
  item: Pick<LineItem, 'description' | 'productId'>,
  index?: number,
  productName?: string,
): string {
  const detail = item.description?.trim() || productName?.trim();
  if (detail) return detail;
  if (index != null) return `Line item ${index + 1}`;
  return 'Line item';
}

/** Post-production status for a line item (dispatch is per line item, not order). */
export function getLineItemFulfillmentStage(
  item: Pick<LineItem, 'workflow' | 'currentStepIndex' | 'dispatch'>,
  order?: Pick<Order, 'deliveryType'>,
): LineItemFulfillmentStage {
  void order;
  if (item.dispatch?.status === 'delivered') return 'delivered';
  if (
    item.dispatch?.status === 'dispatched' ||
    item.dispatch?.status === 'delivery_failed'
  ) {
    return 'dispatched';
  }
  if (allFulfillmentStepsComplete(item as LineItem) && (item.workflow?.length ?? 0) > 0) {
    return 'ready_for_dispatch';
  }
  return 'in_fulfillment';
}

type LineItemPipelineOptions = {
  department?: string;
  /** Queue cards: fulfillment steps only (no dispatch milestones). */
  compact?: boolean;
  /** Dispatch tab: delivery handoff pills only (no Print/QC/etc.). */
  dispatchOnly?: boolean;
  order?: Pick<Order, 'deliveryType'>;
};

/** Fulfillment steps + optional dispatch milestones (Dispatch tab handles delivery). */
export function buildLineItemWorkflowPipeline(
  lineItem: Pick<LineItem, 'workflow' | 'dispatch' | 'deliveryType'>,
  departmentOrOptions?: string | LineItemPipelineOptions,
): LineItemPipelineItem[] {
  const options: LineItemPipelineOptions =
    typeof departmentOrOptions === 'string'
      ? { department: departmentOrOptions }
      : (departmentOrOptions ?? {});
  const { department, compact, dispatchOnly } = options;

  const workflow = lineItem.workflow ?? [];
  const items: LineItemPipelineItem[] = [];
  if (!dispatchOnly) {
    for (let i = 0; i < workflow.length; i++) {
      const step = workflow[i];
      if (!isFulfillmentWorkflowStep(step)) continue;
      if (department && step.department !== department) continue;
      items.push({
        kind: 'workflow',
        stepIndex: i,
        id: step.id,
        label: step.name,
        department: step.department,
      });
    }
  }

  const orderCtx = options.order;
  const deliveryType = orderCtx
    ? getLineItemDeliveryType(orderCtx, lineItem)
    : lineItem.deliveryType;
  const showDeliveryOutcomePills =
    (dispatchOnly || (!compact && !department)) &&
    Boolean(deliveryType) &&
    (lineItem.workflow?.length ?? 0) > 0;

  if (showDeliveryOutcomePills) {
    for (const step of getDeliveryProgressSteps(deliveryType!)) {
      items.push({
        kind: 'dispatch',
        stage: step.id === 'client_pickup' ? 'client_pickup' : step.id,
        id: step.id,
        label: step.label,
      });
    }
  }

  if (!dispatchOnly && (items.length > 0 || showDeliveryOutcomePills) && !compact && !department) {
    items.push({ kind: 'completed', id: 'completed', label: 'Completed' });
  }

  return items;
}

export function getLineItemPipelineItemStatus(
  order: Pick<Order, 'invoice' | 'stage' | 'stageHistory' | 'deliveryType'>,
  lineItem: Pick<LineItem, 'workflow' | 'currentStepIndex' | 'dispatch' | 'deliveryType'>,
  item: LineItemPipelineItem,
): StagePipelineStatus {
  if (!isInvoicingComplete(order) && stageOrder(getEffectivePipelineStage(order)) < stageOrder('in_fulfillment')) return 'pending';

  const liStage = getLineItemFulfillmentStage(lineItem, order);

  if (item.kind === 'workflow') {
    if (liStage !== 'in_fulfillment') return 'done';

    const workflow = lineItem.workflow ?? [];
    const step = workflow[item.stepIndex];
    if (!step) return 'pending';
    if (isSectionComplete(step.status)) return 'done';
    const currentIdx = getEffectiveLineItemStepIndex(lineItem);
    if (item.stepIndex === currentIdx) return 'current';
    if (item.stepIndex < currentIdx) return 'done';
    return 'pending';
  }

  if (item.kind === 'dispatch') {
    if (!allFulfillmentStepsComplete(lineItem as LineItem)) return 'pending';

    const milestone = getLineItemDispatchMilestone(order, lineItem);
    const flow = getDispatchFlowMode(order, lineItem);
    if (flow === 'pickup') {
      if (item.id === 'client_pickup') {
        if (milestone === 'client_confirmed') return 'done';
        if (milestone === 'client_pickup') return 'current';
        return 'pending';
      }
      return 'pending';
    }

    if (item.id === 'dispatched') {
      if (milestone === 'client_confirmed' || milestone === 'delivered' || milestone === 'dispatched') {
        return 'done';
      }
      if (milestone === 'ready_for_dispatch' || milestone === 'delivery_failed') return 'current';
      return 'pending';
    }
    if (item.id === 'delivered') {
      if (milestone === 'client_confirmed') return 'done';
      if (milestone === 'dispatched') return 'current';
      return 'pending';
    }
    return 'pending';
  }

  if (item.kind === 'completed') {
    return isLineItemPipelineComplete(order, lineItem) ? 'done' : 'pending';
  }

  return 'pending';
}

/** @deprecated Use {@link getLineItemPipelineItemStatus} */
export function getLineItemWorkflowStepStatus(
  order: Pick<Order, 'invoice' | 'stage' | 'stageHistory' | 'deliveryType'>,
  lineItem: Pick<LineItem, 'workflow' | 'currentStepIndex' | 'dispatch' | 'deliveryType'>,
  stepIndex: number,
): StagePipelineStatus {
  const pipeline = buildLineItemWorkflowPipeline(lineItem, { order });
  const item = pipeline.find((p) => p.kind === 'workflow' && p.stepIndex === stepIndex);
  if (!item || item.kind !== 'workflow') return 'pending';
  return getLineItemPipelineItemStatus(order, lineItem, item);
}

export function buildWorkflowFromProduct(product: Product | undefined): OrderStep[] {
  if (!product?.processSteps?.length) return [];
  return product.processSteps
    .filter((step) => !isDispatchWorkflowStep(step))
    .map((step) => ({
      id: step.id,
      name: step.name,
      description: step.description,
      department: step.department,
      status: 'queued' as const,
    }));
}

/** True when fulfillment runs per line item (not legacy single order workflow). */
export { orderUsesLineItemWorkflow as usesLineItemWorkflow } from './stageHelpers';

/** Step index for queues/UI — skips legacy dispatch/delivery workflow steps when fulfillment is done. */
export function getEffectiveLineItemStepIndex(
  item: Pick<LineItem, 'workflow' | 'currentStepIndex'>,
): number {
  const workflow = item.workflow ?? [];
  if (!workflow.length) return 0;
  const idx = item.currentStepIndex ?? 0;
  const active = workflow[idx];
  if (!active || !isDispatchWorkflowStep(active)) return idx;
  if (allFulfillmentStepsComplete(item as LineItem)) {
    return Math.max(0, lastFulfillmentStepIndex(workflow));
  }
  for (let i = 0; i < workflow.length; i++) {
    if (isFulfillmentWorkflowStep(workflow[i]) && !isSectionComplete(workflow[i].status)) {
      return i;
    }
  }
  return Math.max(0, lastFulfillmentStepIndex(workflow));
}

export function getActiveLineItemStep(
  item: Pick<LineItem, 'workflow' | 'currentStepIndex'>,
): OrderStep | undefined {
  const idx = getEffectiveLineItemStepIndex(item);
  return item.workflow?.[idx];
}

export function allLineItemWorkflowComplete(item: LineItem): boolean {
  return allFulfillmentStepsComplete(item);
}

/** Every step in the line item workflow array is marked complete (including dispatch steps). */
export function allLineItemWorkflowStepsComplete(
  item: Pick<LineItem, 'workflow'>,
): boolean {
  const wf = item.workflow ?? [];
  if (!wf.length) return false;
  return wf.every((s) => isSectionComplete(s.status));
}

/** Same rules as the line-item Completed progress pill. */
export function isLineItemPipelineComplete(
  order: Pick<Order, 'deliveryType'>,
  item: Pick<LineItem, 'workflow' | 'currentStepIndex' | 'dispatch' | 'deliveryType'>,
): boolean {
  if (!allFulfillmentStepsComplete(item as LineItem)) return false;
  return isLineItemDispatchConfirmed(order, item);
}


/** True when every line item with a workflow satisfies {@link isLineItemPipelineComplete}. */
export function allOrderLineItemsPipelineComplete(
  order: Pick<Order, 'lineItems' | 'deliveryType'>,
): boolean {
  const items = order.lineItems.filter((li) => (li.workflow?.length ?? 0) > 0);
  if (!items.length) return false;
  return items.every((li) => isLineItemPipelineComplete(order, li));
}

export function allOrderLineItemsComplete(order: Pick<Order, 'lineItems'>): boolean {
  const items = order.lineItems.filter((li) => (li.workflow?.length ?? 0) > 0);
  if (!items.length) return false;
  return items.every(allLineItemWorkflowComplete);
}

export type OrderLineItemDeliverySummary = {
  trackable: number;
  delivered: number;
  allDelivered: boolean;
};

export function isLineItemDeliveredForOrderClose(
  _order: Pick<Order, 'deliveryType'>,
  item: LineItem,
): boolean {
  if (!(item.workflow?.length ?? 0)) return true;
  if (!allFulfillmentStepsComplete(item)) return false;
  return item.dispatch?.status === 'delivered';
}

export function getOrderLineItemDeliverySummary(
  order: Pick<Order, 'lineItems' | 'deliveryType'>,
): OrderLineItemDeliverySummary {
  const items = orderLineItemsWithWorkflow(order);
  const delivered = items.filter((li) => isLineItemDeliveredForOrderClose(order, li)).length;
  return {
    trackable: items.length,
    delivered,
    allDelivered: items.length > 0 && delivered === items.length,
  };
}

export function allOrderLineItemsDelivered(order: Pick<Order, 'lineItems' | 'deliveryType'>): boolean {
  return getOrderLineItemDeliverySummary(order).allDelivered;
}

/** Line item workflow + dispatch/delivery finished. */
export function isLineItemComplete(
  item: Pick<LineItem, 'workflow' | 'currentStepIndex' | 'dispatch' | 'deliveryType'>,
  order?: Pick<Order, 'deliveryType'>,
): boolean {
  if (!allFulfillmentStepsComplete(item as LineItem)) return false;
  if (order) return isLineItemDeliveredForOrderClose(order, item as LineItem);
  const delivery = item.deliveryType;
  if (delivery === 'in_store_pickup') return item.dispatch?.status === 'delivered';
  return item.dispatch?.status === 'delivered';
}

/** Order fully finished (terminal stage or every line item delivered). */
export function isOrderComplete(
  order: Pick<Order, 'stage' | 'lineItems' | 'workflow' | 'deliveryType'>,
): boolean {
  if (order.stage === 'delivered' || (order.stage as string) === 'closed') return true;
  if (orderUsesLineItemWorkflow(order)) return allOrderLineItemsDelivered(order);
  return false;
}

export function orderLineItemsWithWorkflow(order: Pick<Order, 'lineItems'>): LineItem[] {
  return order.lineItems.filter((li) => (li.workflow?.length ?? 0) > 0);
}

/** True when this line item's product workflow includes the given department. */
export function lineItemHasWorkflowDepartment(
  item: Pick<LineItem, 'workflow'>,
  department: string,
): boolean {
  return (item.workflow ?? []).some((s) => departmentMatchesWorkflowStep(department, s.department));
}

/** Whether this line item is in scope for a department (workflow step or site install). */
export function lineItemBelongsToDepartment(
  order: Pick<Order, 'deliveryType'>,
  item: LineItem,
  department: string,
): boolean {
  if (department === 'Installation') {
    return lineItemInInstallationScope(order, item);
  }
  if (departmentMatchesWorkflowStep(department, 'Logistics')) {
    return lineItemInLogisticsScope(item);
  }
  return lineItemHasWorkflowDepartment(item, department);
}

export function filterLineItemsForDepartment(
  order: Pick<Order, 'installationRequired' | 'deliveryType' | 'lineItems'>,
  department: string,
): LineItem[] {
  return order.lineItems.filter((li) => lineItemBelongsToDepartment(order, li, department));
}

export function getLineItemAssigneeId(
  item: Pick<LineItem, 'assignments'>,
  department: string,
): string | undefined {
  const assignments = item.assignments;
  if (!assignments) return undefined;
  const canonical = normalizeStepDepartment(department);
  return assignments[canonical] ?? assignments[department];
}

export function lineItemAssignedToUser(
  item: Pick<LineItem, 'assignments'>,
  department: string,
  userId: string,
): boolean {
  return getLineItemAssigneeId(item, department) === userId;
}

type LineItemDeptScope = Pick<
  Order,
  'stage' | 'stageHistory' | 'installationRequired' | 'installation' | 'deliveryType' | 'assignments'
>;

/** Line item is fulfilled on site (not courier-only). */
export function lineItemRequiresSiteInstallation(
  order: Pick<Order, 'deliveryType'>,
  item: Pick<LineItem, 'deliveryType'>,
): boolean {
  const delivery = item.deliveryType ?? order.deliveryType;
  return delivery === 'installation_at_site';
}

/** Product workflow includes Installation and/or on-site delivery. */
export function lineItemInInstallationScope(
  order: Pick<Order, 'deliveryType'>,
  item: Pick<LineItem, 'deliveryType' | 'workflow'>,
): boolean {
  if (lineItemHasWorkflowDepartment(item, 'Installation')) return true;
  return lineItemRequiresSiteInstallation(order, item);
}

function lineItemFulfillmentStarted(
  order: Pick<Order, 'stage'>,
): boolean {
  if (order.stage === 'in_fulfillment' || order.stage === 'on_hold') return true;
  if (['ready_for_dispatch', 'dispatched', 'delivered'].includes(order.stage)) return true;
  return false;
}

/** Site install or workflow Installation step still open on this line item (queue scope). */
export function lineItemInstallationWorkOpen(
  order: LineItemDeptScope,
  item: LineItem,
): boolean {
  if (order.stage === 'cancelled' || order.stage === 'delivered') return false;
  if (!lineItemInInstallationScope(order, item)) return false;
  if (order.installation?.status === 'installed') return false;
  if (!lineItemFulfillmentStarted(order)) return false;

  const installStep = item.workflow?.find((s) => s.department === 'Installation');
  if (installStep) {
    return !isSectionComplete(installStep.status);
  }

  // Site delivery without an Installation step on the product template
  if (!lineItemRequiresSiteInstallation(order, item)) return false;
  if (item.assignments?.Installation || order.assignments?.Installation) return true;
  return allLineItemWorkflowComplete(item);
}

/** Line item is actively on Installation (not merely pre-assigned). */
export function lineItemInstallationActive(
  order: LineItemDeptScope,
  item: LineItem,
): boolean {
  if (order.stage === 'cancelled' || order.stage === 'delivered') return false;
  if (!lineItemInInstallationScope(order, item)) return false;
  if (order.installation?.status === 'installed') return false;
  if (!lineItemFulfillmentStarted(order)) return false;

  const installStep = item.workflow?.find((s) => s.department === 'Installation');
  if (installStep) {
    if (!lineItemActiveStepIncomplete(item, 'Installation')) {
      return false;
    }
    if (order.stage === 'in_fulfillment') return true;
    if (order.stage === 'on_hold') {
      return getEffectivePipelineStage(order) === 'in_fulfillment';
    }
    return false;
  }

  if (!lineItemRequiresSiteInstallation(order, item)) return false;
  return allLineItemWorkflowComplete(item);
}

function lineItemActiveStepIncomplete(item: LineItem, department: string): boolean {
  const step = getActiveLineItemStep(item);
  if (!step || !departmentMatchesWorkflowStep(department, step.department)) return false;
  return !isSectionComplete(step.status);
}

/** Line item is actively waiting on this department. */
export function lineItemInDepartment(
  order: LineItemDeptScope,
  item: LineItem,
  department: string,
): boolean {
  if (order.stage === 'cancelled') return false;
  if (department === 'Installation') {
    return lineItemInstallationActive(order, item);
  }
  if (getLineItemFulfillmentStage(item, order) !== 'in_fulfillment') return false;
  if (!lineItemActiveStepIncomplete(item, department)) return false;
  if (order.stage === 'delivered' || (order.stage as string) === 'closed') return false;
  if (order.stage === 'on_hold') {
    return getEffectivePipelineStage(order) === 'in_fulfillment';
  }
  // Line-item stage is authoritative — order.stage may lag after send-back.
  return true;
}

export function lineItemHasOpenWorkInDepartment(
  order: LineItemDeptScope,
  item: LineItem,
  department: string,
): boolean {
  if (order.stage === 'cancelled') return false;
  if (department === 'Installation') {
    return lineItemInstallationWorkOpen(order, item);
  }
  if (departmentMatchesWorkflowStep(department, 'Logistics')) {
    if (!lineItemInLogisticsScope(item)) return false;
    if (!lineItemFulfillmentStarted(order)) return false;
    const stage = getLineItemFulfillmentStage(item, order);
    return stage === 'ready_for_dispatch' || stage === 'dispatched';
  }
  if (!lineItemFulfillmentStarted(order)) return false;

  const workflow = item.workflow ?? [];
  const deptSteps = workflow.filter((s) => departmentMatchesWorkflowStep(department, s.department));
  if (deptSteps.length === 0) return false;
  if (deptSteps.every((s) => isSectionComplete(s.status))) return false;

  if (getLineItemFulfillmentStage(item, order) !== 'in_fulfillment') return false;
  return true;
}

/** Line items eligible for department assignment (active step or upcoming dept work). */
export function lineItemAssignableInDepartment(
  order: LineItemDeptScope,
  item: LineItem,
  department: string,
): boolean {
  if (lineItemInDepartment(order, item, department)) return true;
  if (department === 'Installation') {
    return lineItemInstallationWorkOpen(order, item);
  }
  if (!lineItemFulfillmentStarted(order)) return false;
  const deptSteps = (item.workflow ?? []).filter((s) => departmentMatchesWorkflowStep(department, s.department));
  if (!deptSteps.length) return false;
  return deptSteps.some((s) => !isSectionComplete(s.status));
}

/**
 * Line item still needs manager attention: active dept work, unassigned open work,
 * or Installation pre-assigned while earlier steps run.
 */
export function lineItemDeptNeedsManagerAttention(
  order: LineItemDeptScope,
  item: LineItem,
  department: string,
): boolean {
  if (!lineItemHasOpenWorkInDepartment(order, item, department)) return false;
  if (lineItemInDepartment(order, item, department)) return true;
  if (department === 'Installation') {
    return lineItemInstallationWorkOpen(order, item);
  }
  if (departmentMatchesWorkflowStep(department, 'Quality Check')) {
    return !getLineItemAssigneeId(item, department);
  }
  return false;
}

export function lineItemVisibleInDeptQueue(
  order: LineItemDeptScope,
  item: LineItem,
  department: string,
  user?: Pick<User, 'id' | 'name' | 'department' | 'isManager' | 'role'> | null,
  _roles?: Pick<Role, 'id' | 'kind'>[],
  isManager?: (u: typeof user, dept: string) => boolean,
  assignedToUser?: (item: LineItem, dept: string, userId: string, user?: Pick<User, 'name'>) => boolean,
): boolean {
  if (!lineItemHasOpenWorkInDepartment(order, item, department)) return false;
  if (!user) return false;

  if (isManager?.(user, department)) {
    return lineItemDeptNeedsManagerAttention(order, item, department);
  }

  if (!lineItemInDepartment(order, item, department)) return false;
  if (assignedToUser?.(item, department, user.id, user)) return true;
  return deptAssignedToUserForQueue(order, department, user.id, user, item);
}

export function expandOrderLineItemsForDepartment(
  orders: Order[],
  _department: string,
  visible: (
    order: Order,
    item: LineItem,
    user?: Pick<User, 'id' | 'name' | 'department' | 'isManager' | 'role'> | null,
    roles?: Pick<Role, 'id' | 'kind'>[],
  ) => boolean,
  user?: Pick<User, 'id' | 'name' | 'department' | 'isManager' | 'role'> | null,
  roles?: Pick<Role, 'id' | 'kind'>[],
): { order: Order; lineItem: LineItem }[] {
  const entries: { order: Order; lineItem: LineItem }[] = [];
  for (const order of orders) {
    if (!orderUsesLineItemWorkflow(order)) continue;
    for (const lineItem of order.lineItems) {
      if (visible(order, lineItem, user, roles)) {
        entries.push({ order, lineItem });
      }
    }
  }
  return entries;
}

export function expandLineItemsForDispatchStage(
  orders: Order[],
  stage: LineItemFulfillmentStage,
): { order: Order; lineItem: LineItem }[] {
  const entries: { order: Order; lineItem: LineItem }[] = [];
  for (const order of orders) {
    if (order.stage === 'cancelled') continue;
    if (!orderUsesLineItemWorkflow(order)) continue;
    for (const lineItem of order.lineItems) {
      if (!(lineItem.workflow?.length ?? 0)) continue;
      if (!lineItemNeedsDispatch(order, lineItem)) continue;
      if (getLineItemFulfillmentStage(lineItem, order) === stage) {
        entries.push({ order, lineItem });
      }
    }
  }
  return entries;
}

export function patchLineItemDispatch(
  lineItem: LineItem,
  dispatch: DispatchInfo,
): LineItem {
  return { ...lineItem, dispatch };
}

/** Open in-fulfillment assignments for a department (matches server workload counts). */
export function countDeptEmployeeWorkload(
  orders: Order[],
  department: string,
  employeeId: string,
): number {
  let count = 0;
  for (const o of orders) {
    if (['closed', 'delivered', 'cancelled'].includes(o.stage)) continue;
    if (orderUsesLineItemWorkflow(o)) {
      for (const li of o.lineItems) {
        if (getLineItemFulfillmentStage(li) !== 'in_fulfillment') continue;
        if (li.assignments?.[department] === employeeId) count++;
      }
    } else if (o.assignments?.[department] === employeeId) {
      count++;
    }
  }
  return count;
}
